/**
 * Client-side noise suppression for InnoCalls calling — DISLIX cleans the
 * agent's mic audio itself, in-browser, before InnoCalls' closed-SDK ever
 * captures it, rather than relying on whatever (if any) suppression
 * InnoCalls' own internal getUserMedia call applies. Same principle Krisp
 * uses commercially (denoise before a black-box calling app captures audio)
 * and the same architecture Jitsi Meet ships in production (RNNoise in an
 * AudioWorklet) — see the plan this was built from for the full rationale.
 *
 * Mechanism: monkey-patch the GLOBAL navigator.mediaDevices.getUserMedia,
 * installed before InnoCalls' SDK script loads (mirrors the existing,
 * proven patchRTCPeerConnectionForAnsweredDetection() precedent in
 * phone-dialer.service.ts — same "patch a global browser API before the
 * third-party script executes" pattern). When InnoCalls' SDK calls
 * getUserMedia internally, this intercepts it, runs the raw stream through
 * an RNNoise AudioWorklet (public/assets/audio/rnnoise-processor.js), and
 * returns the cleaned stream — InnoCalls never knows the difference.
 *
 * Patch INSTALLATION is eager (called once at app boot, alongside the
 * RTCPeerConnection patch). AudioContext/graph CONSTRUCTION is lazy — it
 * only happens the first time the patched getUserMedia is actually invoked,
 * which lines up with the user's "Dial" click (InnoCalls' own capture is
 * triggered by startCall()), sidestepping the autoplay-suspended-AudioContext
 * gotcha (Chrome/Edge start every AudioContext suspended until a user
 * gesture — creating one eagerly at boot, far from any gesture, would leave
 * it silently stuck suspended).
 */

const WORKLET_PROCESSOR_URL = '/assets/audio/rnnoise-processor.js';
const RNNOISE_WASM_URL = '/assets/audio/rnnoise.wasm';
const LOG_TAG = '[NoiseSuppression]';

let installed = false;
let audioContext: AudioContext | null = null;
let workletModuleLoaded: Promise<void> | null = null;
let wasmBytesPromise: Promise<ArrayBuffer> | null = null;
let originalGetUserMedia: typeof navigator.mediaDevices.getUserMedia | null = null;
// The RAW hardware mic stream(s) captured by our patched getUserMedia. We
// return a SYNTHETIC stream (built from a MediaStreamAudioDestinationNode)
// to the caller — InnoCalls' SDK only ever sees and holds that synthetic
// track, never the real one, so it can never call .stop() on the actual
// hardware capture itself when a call ends. Without tracking and explicitly
// stopping these ourselves, the real mic stays captured (browser's mic-in-
// use indicator stays lit) for the rest of the page session regardless of
// call state — confirmed as a real production issue. Tracked in a Set
// since InnoCalls could in principle call getUserMedia more than once
// (e.g. across multiple calls without a page reload) before we've released
// an earlier one.
const activeRawStreams = new Set<MediaStream>();

// The Web Audio graph nodes built per call (source -> worklet -> destination).
// Nothing above ever kept a reference to these after buildDenoisedStream
// returned, so a live connection graph — and the RNNoise worklet inside it,
// still running rnnoise_process_frame every render quantum — was never torn
// down between calls. Confirmed as the root cause of a real production
// report: audio (even the ring tone, before any real voice) turning into an
// "underwater"/garbled artifact partway through a session, worsening call
// over call, only clearing on a full page refresh or logout/login (the only
// things that previously destroyed the shared AudioContext). Every call
// after the first was leaving its entire graph running — permanently
// consuming a share of the AudioWorklet thread's per-quantum time budget —
// on top of whichever call is actually active, and the accumulation
// compounds with every subsequent call placed in the same session. Tracked
// here so finalizeCall's existing releaseActiveMicStreams() can disconnect
// each call's graph nodes, not just stop the raw hardware track.
interface DenoiseGraphNodes {
  source: MediaStreamAudioSourceNode;
  workletNode: AudioWorkletNode;
  destination: MediaStreamAudioDestinationNode;
}
const activeGraphNodes = new Set<DenoiseGraphNodes>();

const ensureAudioContext = (): AudioContext => {
  if (!audioContext || audioContext.state === 'closed') {
    // No custom sampleRate — let it default, matching Chromium's WebRTC/
    // Opus pipeline expectations rather than introducing a resampling step.
    audioContext = new AudioContext();
  }
  return audioContext;
};

const ensureWorkletLoaded = (ctx: AudioContext): Promise<void> => {
  if (!workletModuleLoaded) {
    workletModuleLoaded = ctx.audioWorklet.addModule(WORKLET_PROCESSOR_URL);
  }
  return workletModuleLoaded;
};

// Fetched here (main thread) rather than inside the worklet — confirmed via
// a real production failure that `fetch` is not available inside
// AudioWorkletGlobalScope (ReferenceError: fetch is not defined). The
// downloaded bytes are handed to each new AudioWorkletNode via
// processorOptions instead.
const ensureWasmBytes = (): Promise<ArrayBuffer> => {
  if (!wasmBytesPromise) {
    wasmBytesPromise = fetch(RNNOISE_WASM_URL).then((res) => {
      if (!res.ok) throw new Error(`Failed to fetch RNNoise wasm: ${res.status}`);
      return res.arrayBuffer();
    });
  }
  return wasmBytesPromise;
};

const buildDenoisedStream = async (rawStream: MediaStream): Promise<MediaStream> => {
  const ctx = ensureAudioContext();
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  const [, wasmBytes] = await Promise.all([ensureWorkletLoaded(ctx), ensureWasmBytes()]);

  const source = ctx.createMediaStreamSource(rawStream);
  // channelCount alone does NOT force the graph to mono — that requires
  // channelCountMode: 'explicit' too (the AudioWorkletNode default mode is
  // 'max', which lets the actual channel count follow the loudest/widest
  // input instead of clamping to channelCount). Without this, a stereo mic
  // capture could carry raw, un-denoised audio on a second channel that
  // process() never reads (it only reads inputs[0][0]) but that the browser
  // could still fold into the graph's output via default up/down-mixing —
  // a real, confirmed candidate for "denoised speech is clean but ambient/
  // air noise still comes through," since RNNoise itself only ever sees
  // and cleans channel 0. Forcing explicit mono end-to-end (source,
  // worklet, and destination) guarantees exactly one channel flows through
  // the whole graph and it's the one RNNoise actually processed.
  source.channelCount = 1;
  source.channelCountMode = 'explicit';
  source.channelInterpretation = 'speakers';

  const workletNode = new AudioWorkletNode(ctx, 'rnnoise-processor', {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    channelCount: 1,
    channelCountMode: 'explicit',
    channelInterpretation: 'speakers',
    // Cloned (not transferred) — wasmBytesPromise is cached and reused for
    // every subsequent call in the session, so the buffer must stay usable
    // on the main thread rather than being detached by a structured-clone
    // transfer.
    processorOptions: { wasmBytes: wasmBytes.slice(0) },
  });
  workletNode.port.onmessage = (event) => {
    if (event.data?.type === 'ready') {
      console.log(`${LOG_TAG} RNNoise worklet initialized — denoising is active for this call`);
    } else if (event.data?.type === 'failed') {
      console.warn(`${LOG_TAG} RNNoise worklet failed to initialize — audio passing through unprocessed:`, event.data.error);
    }
  };
  const destination = ctx.createMediaStreamDestination();
  destination.channelCount = 1;
  destination.channelCountMode = 'explicit';
  destination.channelInterpretation = 'speakers';
  source.connect(workletNode).connect(destination);

  const outStream = new MediaStream();
  destination.stream.getAudioTracks().forEach((t) => outStream.addTrack(t));
  // Defensive: mirror any non-audio tracks from the raw stream unchanged —
  // should never occur for the {audio:true}-only requests this app's
  // calling flow makes, but don't silently drop them if that ever changes.
  rawStream.getTracks().forEach((t) => {
    if (t.kind !== 'audio') outStream.addTrack(t);
  });

  // Track so releaseActiveMicStreams() can actually stop the real hardware
  // capture on call end — see the activeRawStreams doc comment above for
  // why this is necessary (InnoCalls never receives a reference to
  // rawStream, only to outStream, so it can never release the mic itself).
  activeRawStreams.add(rawStream);
  // Track so releaseActiveMicStreams() can disconnect this call's graph —
  // see the activeGraphNodes doc comment above.
  const graphNodes: DenoiseGraphNodes = { source, workletNode, destination };
  activeGraphNodes.add(graphNodes);

  return outStream;
};

/** Stops every tracked raw mic stream — releases the real hardware
 * capture (browser's mic-in-use indicator turns off) once a call actually
 * ends. Called from usePhoneDialer's finalizeCall, the single choke point
 * every call-end path (SDK event or agent hangup) already funnels through.
 * Safe to call even if denoising was never engaged (e.g. it fell back to
 * raw passthrough, or no call was ever placed) — the set is simply empty. */
export const releaseActiveMicStreams = (): void => {
  activeRawStreams.forEach((stream) => {
    stream.getTracks().forEach((track) => track.stop());
  });
  activeRawStreams.clear();

  // Tear down every call's denoise graph — without this, each call's
  // source/worklet/destination nodes stayed connected and running
  // indefinitely (see activeGraphNodes doc comment), degrading every
  // subsequent call in the session. Disconnecting also lets the
  // AudioWorkletProcessor instance itself get garbage collected once its
  // port is unreferenced, stopping its process() calls for good.
  activeGraphNodes.forEach(({ source, workletNode, destination }) => {
    try {
      // Tell the processor instance to free its WASM state and stop doing
      // work BEFORE disconnecting — see rnnoise-processor.js's 'stop'
      // message handler for why postMessage, not port.close(), is used here.
      workletNode.port.postMessage({ type: 'stop' });
      source.disconnect();
      workletNode.disconnect();
      destination.disconnect();
    } catch (err) {
      console.warn(`${LOG_TAG} Error tearing down denoise graph nodes:`, err);
    }
  });
  activeGraphNodes.clear();
};

// Confirmed real production issue: calls placed from the native app (which
// loads CallScreen.tsx inside react-native-webview's WKWebView, not a real
// Safari/Chrome tab) developed one-directional audio corruption — the
// agent's OUTGOING mic audio (the only path this denoising pipeline ever
// touches; incoming remote audio bypasses it entirely and was unaffected)
// degraded and eventually stopped after roughly 10 seconds, while the same
// pipeline has no such issue on desktop/mobile-web calls (a real browser
// tab). Leading theory: WKWebView's JS/WASM engine is measurably slower for
// this kind of sustained real-time AudioWorklet processing than a full
// browser, and once the worklet starts missing its ~2.7ms render-quantum
// budget under that load, the underlying AudioContext/node can degrade —
// something this pipeline's own extensive RingBuffer/prebuffer machinery
// (see rnnoise-processor.js) can't fully compensate for at the engine level.
// Rather than risk shipping a partial fix for a black-box SDK + WASM combo
// in an environment we can't directly profile, this disables denoising
// ENTIRELY for WebView-hosted calls — window.ReactNativeWebView is injected
// ONLY by react-native-webview (confirmed already used identically for
// CallScreen.tsx's own dismiss-bridge detection), never present in a real
// browser tab, so this cleanly targets exactly "the mobile app is the
// source" without affecting desktop/mobile-web calling at all, where this
// pipeline is already proven to work. Raw, unprocessed mic audio (no
// RNNoise, no AGC) is a strictly better outcome than corrupted/dying audio.
const isHostedInNativeWebView = (): boolean =>
  typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;

export const installNoiseSuppressedGetUserMedia = (): void => {
  if (installed) return;
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
  if (isHostedInNativeWebView()) {
    console.log(`${LOG_TAG} Skipping install — running inside the native app's WebView, where this pipeline is confirmed unreliable; calls placed from the mobile app use raw, unprocessed mic audio instead.`);
    return;
  }
  installed = true;

  // Must .bind(navigator.mediaDevices) — the native method is this-
  // sensitive; calling it detached throws "Illegal invocation".
  originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

  navigator.mediaDevices.getUserMedia = async (constraints?: MediaStreamConstraints): Promise<MediaStream> => {
    const raw = await originalGetUserMedia!(constraints);

    const wantsAudio = !!constraints?.audio;
    if (!wantsAudio || raw.getAudioTracks().length === 0) return raw;

    try {
      const denoised = await buildDenoisedStream(raw);
      console.log(`${LOG_TAG} getUserMedia intercepted — returning RNNoise-processed stream`);
      return denoised;
    } catch (err) {
      // ANY failure here must fall back to the raw stream — noise
      // suppression must never break calling.
      console.warn(`${LOG_TAG} Falling back to raw microphone stream — denoising pipeline failed:`, err);
      return raw;
    }
  };

  console.log(`${LOG_TAG} navigator.mediaDevices.getUserMedia patched`);
};

/** Closes the shared AudioContext — called from usePhoneDialer's unmount
 * effect alongside destroyInnoCallsInstance, on full dialer/session teardown
 * (not per-call — the context is reused across calls in a session). */
export const teardownNoiseSuppression = (): void => {
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close().catch(() => {});
  }
  audioContext = null;
  workletModuleLoaded = null;
  wasmBytesPromise = null;
};
